import React from 'react';

import type { IconLookup, IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import kebabCase from 'lodash/kebabCase';
export interface ReportItem {
    title: string;
    link: string;
    body: string;
    icon: React.ComponentType<any>;
    permissionId: string;
}

export interface ReportCategory {
    title: string;
    icon: IconName | IconLookup; // Ensures the string is a valid FontAwesome icon name
    items: ReportItem[];
}
export const REPORT_CATEGORIES: ReportCategory[] = [
    {
        title: 'Operations',
        icon: 'suitcase',
        items: [
            {
                title: 'Tours Details',
                link: kebabCase('Tours Details'),
                body: 'Tour details and travel files overview',
                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'tag']} />,
                permissionId: 'report.tour_details:view'
            },
            {
                title: 'Tours Registration',
                link: kebabCase('Tours Registration'),
                body: 'Tour registration and booking status',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'tag']} />,
                permissionId: 'report.tour_registration:view'
            },
            {
                title: 'Travel File By Inventory',
                link: kebabCase('inventory'),
                body: 'Inventory analysis by tour',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'boxes']} />,
                permissionId: 'report.travel_file_inventory:view'
            },
            {
                title: 'Reservations',
                link: 'reservations',
                body: 'Reservation history and details',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'calendar-check']} />,
                permissionId: 'report.reservations:view'
            }
        ]
    },
    {
        title: 'Sales & Revenue',
        icon: 'chart-line',
        items: [
            {
                title: 'Sales & Profit',
                link: 'sales-profit',
                body: 'Sales and profit by service transaction',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'money-check-alt']} />,
                permissionId: 'report.sales:view'
            },
            {
                title: 'Sales & Profit Summary',
                link: 'sales-profit-summary',
                body: 'Sales and profit summary by currency',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'money-check-alt']} />,
                permissionId: 'report.sales:view'
            }
        ]
    },
    {
        // The service team's own shelf: chasing money owed and handling
        // cancellations. Both reports previously sat in categories organised
        // around the data (Operations / Financial) rather than around who
        // actually works them.
        title: 'Service',
        icon: 'headset',
        items: [
            {
                title: 'Left to Pay',
                link: kebabCase('left to pay'),
                body: 'Outstanding balances with customer contact details',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'hand-holding-usd']} />,
                permissionId: 'report.left_to_pay:view'
            },
            {
                title: 'Cancelled Passengers',
                link: kebabCase('Cancelled Passengers'),
                body: 'Passenger cancellations by date, tour and clerk',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'user-times']} />,
                permissionId: 'report.cancelled_passengers:view'
            }
        ]
    },
    {
        title: 'Financial',
        icon: 'file-invoice-dollar',
        items: [
            {
                title: 'RCT/TRC',
                link: kebabCase('rct trc'),
                body: 'Receipts and transfer receipts',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'receipt']} />,
                permissionId: 'report.rct_trc:view'
            },
            {
                title: 'PRQ/RMT',
                link: kebabCase('prq rmt'),
                body: 'Payment requests and remittances',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'file-invoice']} />,
                permissionId: 'report.prq_rmt:view'
            },
            {
                title: 'Documents',
                link: 'documents',
                body: 'Search and filter all document types',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'file-alt']} />,
                permissionId: 'report.documents:view'
            },
            {
                title: 'Exchange Rates',
                link: kebabCase('exchange rates'),
                body: 'Currency exchange rate changes',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'exchange-alt']} />,
                permissionId: 'report.exchange_rates:view'
            }
        ]
    },
    {
        title: 'Supplier',
        icon: 'truck',
        items: [
            {
                title: 'Supplier Turnover',
                link: kebabCase('supplier turnover'),
                body: 'Supplier turnover details',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'handshake']} />,
                permissionId: 'report.supplier_turnover:view'
            },
            {
                title: 'Supplier Turnover Summary',
                link: kebabCase('supplier turnover summary'),
                body: 'Supplier turnover aggregated summary',

                icon: (props) => <FontAwesomeIcon {...props} icon={['fa', 'handshake']} />,
                permissionId: 'report.supplier_turnover_summary:view'
            }
        ]
    }
] as const;
